import React, { useState, useEffect } from 'react'
import { Plus, Trash2, Bell, BellOff, RefreshCw } from 'lucide-react'
import { alertsAPI } from '../services/api'
import toast from 'react-hot-toast'

const RULE_TYPES = [
  { value: 'concentration',      label: 'Concentration %',   scope: 'holding',   defaultThreshold: 20, direction: 'above', hint: 'Single holding exceeds % of book' },
  { value: 'holding_loss',       label: 'Holding loss %',    scope: 'holding',   defaultThreshold: -8, direction: 'below', hint: 'A holding falls below this P&L%' },
  { value: 'trailing_stop',      label: 'Trailing stop %',   scope: 'holding',   defaultThreshold: -10, direction: 'below', hint: 'A holding P&L% breaches stop' },
  { value: 'portfolio_drawdown', label: 'Portfolio drawdown %', scope: 'portfolio', defaultThreshold: 8, direction: 'above', hint: 'Whole book drops from peak' },
  { value: 'health_score',       label: 'Health score below', scope: 'holding',  defaultThreshold: 50, direction: 'below', hint: 'Composite score drops below' },
  { value: 'news_flag',          label: 'News flag',         scope: 'holding',   defaultThreshold: 1, direction: 'above', hint: 'Negative news on a holding' },
]

const EMPTY = { name: '', type: 'concentration', scope: 'holding', symbol: '', threshold: 12, direction: 'above', severity: 'warning', suggestOrder: true, etfExempt: true, cooldownHours: 24, enabled: true }

const AlertRulesPanel = () => {
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [seeding, setSeeding] = useState(false)

  const load = async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const res = await alertsAPI.getRules()
      setRules(res.data.rules || [])
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load'
      setLoadError(msg)
      toast.error('Failed to load alert rules: ' + msg)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  const seedDefaults = async (force = false) => {
    if (force) {
      if (!window.confirm('This will DELETE all existing rules (including any test rules) and load the 8 smart defaults. Continue?')) return
    } else if (rules.length > 0 && !window.confirm('You already have rules. Replace all with defaults?')) return
    setSeeding(true)
    try {
      const res = await alertsAPI.seedDefaults(force)   // pass force directly — was backwards before
      if (res.data.seeded) { toast.success(`${res.data.count} default rules loaded`); load() }
      else toast.success('Default rules are already loaded')
    } catch { toast.error('Failed to seed defaults') }
    finally { setSeeding(false) }
  }

  const pickType = (typeVal) => {
    const t = RULE_TYPES.find(r => r.value === typeVal)
    setForm(f => ({
      ...f, type: typeVal, scope: t.scope,
      threshold: t.defaultThreshold, direction: t.direction,
      name: f.name || t.label,
    }))
  }

  const save = async () => {
    if (!form.name.trim()) { toast.error('Give the rule a name'); return }
    setSaving(true)
    try {
      await alertsAPI.createRule({ ...form, symbol: form.symbol.trim().toUpperCase() || null })
      toast.success('Rule created')
      setForm(EMPTY); setShowForm(false); load()
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to create rule') }
    finally { setSaving(false) }
  }

  const toggle = async (rule) => {
    try {
      await alertsAPI.updateRule(rule._id, { enabled: !rule.enabled })
      setRules(rs => rs.map(r => r._id === rule._id ? { ...r, enabled: !r.enabled } : r))
    } catch { toast.error('Failed to toggle') }
  }

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return
    try {
      await alertsAPI.deleteRule(rule._id)
      setRules(rs => rs.filter(r => r._id !== rule._id))
      toast.success('Rule deleted')
    } catch { toast.error('Failed to delete') }
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Alert Rules</h3>
        </div>
        <div className="flex gap-2">
          {rules.length === 0 && (
            <button onClick={() => seedDefaults(false)} disabled={seeding} className="text-sm text-success-600 hover:text-success-700 bg-success-50 hover:bg-success-100 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60">
              {seeding ? 'Loading…' : '✦ Load Defaults'}
            </button>
          )}
          <button onClick={() => setShowForm(s => !s)} className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-colors">
            <Plus className="w-4 h-4" /> New Rule
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Rule name</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="input w-full text-sm" placeholder="e.g. Concentration > 20%" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Type</label>
              <select value={form.type} onChange={e => pickType(e.target.value)} className="input w-full text-sm">
                {RULE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Threshold</label>
              <input type="number" value={form.threshold} onChange={e => setForm(f => ({ ...f, threshold: parseFloat(e.target.value) }))} className="input w-full text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Symbol <span className="text-gray-400">(blank = all holdings)</span></label>
              <input value={form.symbol} onChange={e => setForm(f => ({ ...f, symbol: e.target.value.toUpperCase() }))} className="input w-full text-sm font-mono" placeholder="e.g. RELIANCE" disabled={form.scope === 'portfolio'} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            {form.type === 'concentration' && (
              <label className="flex items-center gap-1.5 text-success-700">
                <input type="checkbox" checked={form.etfExempt !== false} onChange={e => setForm(f => ({ ...f, etfExempt: e.target.checked }))} />
                ETF-exempt (recommended)
              </label>
            )}
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={form.suggestOrder} onChange={e => setForm(f => ({ ...f, suggestOrder: e.target.checked }))} />
              Suggest one-click sell
            </label>
            <label className="flex items-center gap-1.5">
              Severity
              <select value={form.severity || 'warning'} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))} className="input text-sm py-1 w-28">
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
                <option value="info">Info</option>
              </select>
            </label>
            <label className="flex items-center gap-1.5">
              Cooldown
              <input type="number" value={form.cooldownHours} onChange={e => setForm(f => ({ ...f, cooldownHours: parseInt(e.target.value) || 0 }))} className="input w-16 text-sm py-1" /> h
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowForm(false); setForm(EMPTY) }} className="btn-secondary text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary text-sm">{saving ? 'Saving…' : 'Create Rule'}</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
          <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading rules…
        </div>
      ) : loadError ? (
        <div className="py-4 text-center text-sm text-danger-600">
          Error loading rules: {loadError}
          <button onClick={load} className="ml-2 underline text-primary-600">Retry</button>
        </div>
      ) : rules.length === 0 ? (
        <div className="py-6 text-center space-y-2">
          <p className="text-sm text-gray-400">No alert rules yet.</p>
          <button onClick={() => seedDefaults(false)} disabled={seeding} className="text-sm text-success-600 hover:text-success-700 bg-success-50 hover:bg-success-100 px-4 py-2 rounded-lg transition-colors font-medium disabled:opacity-60">
            {seeding ? 'Loading…' : '✦ Load 8 smart defaults'}
          </button>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {rules.map(rule => (
            <div key={rule._id} className="flex items-center justify-between py-2.5">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-gray-800">{rule.name}</span>
                  {rule.symbol && <span className="text-xs font-mono text-gray-400">{rule.symbol}</span>}
                  {rule.etfExempt && rule.type === 'concentration' && (
                    <span className="text-xs bg-success-100 text-success-700 px-1.5 py-0.5 rounded">ETF-exempt</span>
                  )}
                  {rule.severity === 'critical' && (
                    <span className="text-xs bg-danger-100 text-danger-700 px-1.5 py-0.5 rounded">critical</span>
                  )}
                  {!rule.enabled && <span className="text-xs text-gray-400">(paused)</span>}
                </div>
                <span className="text-xs text-gray-500">
                  {rule.type.replace(/_/g, ' ')} {rule.direction} {rule.threshold}
                  {rule.suggestOrder && ' · suggests order'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => toggle(rule)} className="p-1.5 text-gray-400 hover:text-primary-600 rounded" title={rule.enabled ? 'Pause' : 'Enable'}>
                  {rule.enabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                </button>
                <button onClick={() => remove(rule)} className="p-1.5 text-gray-400 hover:text-danger-600 rounded" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {rules.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end">
          <button
            onClick={() => seedDefaults(true)}
            disabled={seeding}
            className="text-xs text-gray-400 hover:text-danger-600 transition-colors"
            title="Delete all rules and reload the 8 sensible defaults"
          >
            {seeding ? 'Resetting…' : 'Reset to defaults'}
          </button>
        </div>
      )}
    </div>
  )
}

export default AlertRulesPanel
